import { useEffect, useRef, type RefObject } from 'react';

export type DismissReason = 'escape' | 'outside';

/**
 * Closes a menu or popover the three ways people expect: Escape, a press
 * anywhere outside it, or focus moving elsewhere (Tab, a click on another
 * control). `ref` must wrap the trigger as well as the panel, so pressing the
 * trigger toggles instead of closing and instantly reopening.
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onDismiss: (reason: DismissReason) => void,
): void {
  // Kept in a ref so an inline callback does not re-bind the document
  // listeners on every render.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onDismissRef.current = onDismiss; });

  useEffect(() => {
    if (!isOpen) return undefined;

    const isOutside = (target: EventTarget | null) => (
      target instanceof Node && !ref.current?.contains(target)
    );
    const dismissIfOutside = (event: Event) => {
      if (isOutside(event.target)) onDismissRef.current('outside');
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current('escape');
    };

    document.addEventListener('pointerdown', dismissIfOutside);
    document.addEventListener('focusin', dismissIfOutside);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissIfOutside);
      document.removeEventListener('focusin', dismissIfOutside);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [isOpen, ref]);
}
